import {useId} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Tooltip from 'components/common/tooltip';
import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';
import {useCalculationReadOnly} from 'components/cabinet/calculator/calculation-read-only';
import {useInformers} from 'api/informers';
import {COLORS, DECLARATION_FORM, INFORMERS_CODES} from 'config/constants';
import CustomsProperty from './property';

const CustomsCertificate = ({isEditing, onEdit, onChange}: CustomsCertificateProps) => {
  const {t} = useTranslation();

  const {control, watch} = useFormContext<DeclarationFormFields>();
  const labelId = useId();
  const readOnly = useCalculationReadOnly();
  const informers = useInformers();

  const value = watch(DECLARATION_FORM.ORIGIN_CERTIFICATE);
  const label = t('calc__text__customs__certificateOfOrigin');
  const yesLabel = t('calc__text__customs__certificateOfOriginYes');
  const noLabel = t('calc__text__customs__certificateOfOriginNo');
  const valueLabel = value ? yesLabel : noLabel;

  if (!isEditing) {
    return (
      <CustomsProperty
        label={label}
        value={
          <Typography
            component='button'
            type='button'
            variant='inherit'
            disabled={readOnly}
            onClick={onEdit}
            sx={{
              border: 0,
              padding: 0,
              background: 'transparent',
              textDecoration: 'underline dotted',
              textUnderlineOffset: '3px',
              color: readOnly ? COLORS.BLACK_DARK : COLORS.BLUE,
              cursor: 'pointer',
              lineHeight: '21px',
              '&:hover': {
                color: COLORS.BLUE_DARK,
              },
            }}
          >
            {formatDemoCopy(valueLabel)}
          </Typography>
        }
      />
    );
  }

  return (
    <FormControl fullWidth disabled={readOnly}>
      <InputLabel id={labelId} shrink>
        <Typography component='span' variant='inherit'>{formatDemoCopy(label)}</Typography>
      </InputLabel>
      <Controller
        name={DECLARATION_FORM.ORIGIN_CERTIFICATE}
        control={control}
        render={({field}) => (
          <Select
            {...field}
            value={String(field.value)}
            labelId={labelId}
            label={label}
            notched
            sx={{backgroundColor: COLORS.WHITE}}
            onChange={event => {
              const checked = event.target.value === 'true';

              field.onChange(checked);
              onChange(checked);
            }}
            renderValue={() => (
              <div className='customs-declaration__certificate-value'>
                <Tooltip content={informers?.[INFORMERS_CODES.ORIGIN_CERTIFICATE]} />
                <Typography component='span' variant='body1' sx={{
                  lineHeight: '24px',
                  letterSpacing: 0,
                  color: COLORS.BLACK_DARK,
                }}>
                  {formatDemoCopy(valueLabel)}
                </Typography>
              </div>
            )}
          >
            <MenuItem value='true'>
              <Typography variant='body1'>{formatDemoCopy(yesLabel)}</Typography>
            </MenuItem>
            <MenuItem value='false'>
              <Typography variant='body1'>{formatDemoCopy(noLabel)}</Typography>
            </MenuItem>
          </Select>
        )}
      />
    </FormControl>
  );
};

export default CustomsCertificate;
